import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Avatar from '../components/Avatar';
import PostCard from '../components/PostCard';
import LiveCard from '../components/LiveCard';
import LiveFeedCard from '../components/LiveFeedCard';
import SystemFeedCard from '../components/SystemFeedCard';
import { useAuth } from '../context/AuthContext';
import { discoverLives } from '../api/lives';
import { homeFeed, homeStories, followSuggestions, toggleFollow } from '../api/social';
import { Camera, Radio, ChevronRight, Calendar, UserPlus, BadgeCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { recordMediaEvent, setTelemetryPage } from '../lib/mediaTelemetry';
import PeopleFeed from '../components/PeopleFeed';
import useAppConfig from '../hooks/useAppConfig';

const LiveStoryUnused = null; // removed: replaced by StoryRing (Phase 25). Kept as named export shim only if external imports exist.
export { LiveStoryUnused };

/** Stories ring entry — followed creators with activity, or (0 follows)
    a random pool of active live/O2O/scheduled creators.
    Ring colors: live=red · o2o=green · scheduled/post=gold gradient. */
const StoryRing = ({ story }) => {
  const navigate = useNavigate();
  const stype = story.story_type || (story.is_live ? 'live' : 'post');
  const target = stype === 'o2o'
    ? `/u/${story.username}`
    : (story.live_id ? `/lives/${story.live_id}` : `/u/${story.username}`);
  const ringClass = stype === 'live'
    ? 'bg-gradient-to-tr from-red-500 via-rose-500 to-pink-600 animate-pulse'
    : stype === 'o2o'
      ? 'bg-gradient-to-tr from-emerald-400 via-green-500 to-teal-500'
      : 'bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600';
  const testId = stype === 'live'
    ? 'story-ring-live'
    : stype === 'o2o'
      ? 'story-ring-o2o'
      : stype === 'scheduled'
        ? 'story-ring-scheduled'
        : 'story-ring-recent';
  return (
    <button
      onClick={() => navigate(target)}
      className="flex w-16 shrink-0 flex-col items-center gap-1"
      data-testid={testId}
    >
      <div className="relative">
        <div className={`rounded-full p-[2.5px] ${ringClass}`}>
          <div className="rounded-full bg-card p-[2px]">
            <Avatar src={story.avatar_url} name={story.username} size={56} />
          </div>
        </div>
        {stype === 'live' && (
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-md bg-red-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow">
            LIVE
          </span>
        )}
        {stype === 'o2o' && (
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-md bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow">
            1:1
          </span>
        )}
        {stype === 'scheduled' && (
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-center rounded-md bg-blue-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow">
            <Calendar className="mr-0.5 h-2.5 w-2.5" />
          </span>
        )}
      </div>
      <span className="mt-1 max-w-[64px] truncate text-xs font-semibold text-foreground">{story.username}</span>
    </button>
  );
};

const UpcomingLivesBanner = ({ upcoming }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('home');
  if (!upcoming?.length) return null;
  return (
    <div className="mb-4 rounded-lg border border-amber-500/20 bg-gradient-to-r from-amber-900/20 via-neutral-900 to-violet-900/20 px-3 py-3 md:rounded-lg">
      <div className="mb-2 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide gold-text">
          <Calendar className="h-3.5 w-3.5" /> {t('upcoming_lives.title')}
        </p>
        <button
          onClick={() => navigate('/lives')}
          className="flex items-center gap-0.5 text-xs font-semibold gold-text hover:text-amber-200"
        >
          {t('upcoming_lives.see_all')} <ChevronRight className="h-3 w-3" />
        </button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {upcoming.slice(0, 6).map((live) => {
          const d = new Date(live.scheduled_at);
          return (
            <button
              key={live.id}
              onClick={() => navigate(`/lives/${live.id}`)}
              className="flex w-44 shrink-0 flex-col items-start rounded-lg border border-border bg-card p-2 text-left shadow-sm hover:border-amber-400/60"
            >
              <div className="flex w-full items-center justify-between">
                <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-blue-300">
                  {live.kind}
                </span>
                {live.is_paid ? (
                  <span className="text-[10px] font-bold gold-text">{live.price} {t('upcoming_lives.credits_short')}</span>
                ) : (
                  <span className="text-[10px] font-bold text-emerald-400">{t('upcoming_lives.free')}</span>
                )}
              </div>
              <p className="mt-1 line-clamp-2 text-xs font-semibold text-foreground">{live.title}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">@{live.creator?.username}</p>
              <p className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                {d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ·{' '}
                {d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Suggestion = ({ s, onFollow }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('home');
  return (
    <div className="flex items-center justify-between py-2" data-testid="suggestion-item">
      <button
        onClick={() => navigate(`/u/${s.username}`)}
        className="flex items-center gap-3 text-left"
      >
        <Avatar src={s.avatar_url} name={s.username} size={36} />
        <div className="flex flex-col leading-tight">
          <span className="flex items-center gap-1 text-sm font-semibold">
            {s.username}
            {s.is_verified_creator && <BadgeCheck className="h-3.5 w-3.5 gold-text" />}
          </span>
          <span className="text-xs text-muted-foreground">
            {s.full_name || t('sidebar.followers_one', { count: s.followers_count, defaultValue_plural: t('sidebar.followers_other', { count: s.followers_count }) })}
          </span>
        </div>
      </button>
      <button
        onClick={() => onFollow(s)}
        className="text-xs font-semibold gold-text hover:underline"
        data-testid="suggestion-follow-btn"
      >
        {t('sidebar.follow')}
      </button>
    </div>
  );
};

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('home');
  // Phase F1 — Voice-First People Feed flag: null=unknown, true=people, false=classic
  const { loaded: cfgLoaded, peopleFeedEnabled } = useAppConfig();
  const peopleMode = cfgLoaded ? peopleFeedEnabled : null;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveData, setLiveData] = useState({ live_now: [], upcoming: [], past: [] });
  const [stories, setStories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  // Phase 49 · Feed perf — progressive rendering. Keep the first
  // `visibleCount` items in the DOM; grow when the sentinel enters
  // the viewport. This caps how many <video> and <img> tags exist
  // at once, drastically improving mobile scroll FPS.
  const INITIAL_PAGE = 8;
  const PAGE_STEP = 8;
  const [visibleCount, setVisibleCount] = useState(INITIAL_PAGE);
  const sentinelRef = useRef(null);
  // NeoUI — first-visit tooltip on the stories ring ("Tocca per entrare").
  const [showRingTip, setShowRingTip] = useState(false);
  const dismissRingTip = useCallback(() => {
    try { localStorage.setItem('nymia_ring_tip_seen', '1'); } catch (_e) { /* noop */ }
    setShowRingTip(false);
  }, []);
  useEffect(() => {
    let seen = true;
    try { seen = !!localStorage.getItem('nymia_ring_tip_seen'); } catch (_e) { /* noop */ }
    if (stories.length > 0 && !seen) setShowRingTip(true);
  }, [stories.length]);
  useEffect(() => {
    if (!showRingTip) return undefined;
    const timer = setTimeout(dismissRingTip, 8000);
    return () => clearTimeout(timer);
  }, [showRingTip, dismissRingTip]);

  const loadFeed = useCallback(async () => {
    try {
      const [feed, lives, st, sug] = await Promise.all([
        homeFeed(30),
        discoverLives(),
        homeStories().catch(() => []),
        followSuggestions(5).catch(() => []),
      ]);
      setItems(feed.items || []);
      setLiveData(lives);
      setStories(Array.isArray(st) ? st : []);
      setSuggestions(Array.isArray(sug) ? sug : []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const onFollowSuggestion = async (s) => {
    try {
      await toggleFollow(s.username);
      setSuggestions((prev) => prev.filter((x) => x.user_id !== s.user_id));
      const fresh = await homeStories().catch(() => []);
      setStories(Array.isArray(fresh) ? fresh : []);
    } catch (e) {
      // silent — the user can retry
    }
  };

  useEffect(() => {
    if (peopleMode !== false) return undefined;
    loadFeed();
    recordMediaEvent('feed_views');
    setTelemetryPage('feed');
    // Phase 49 · Killed the aggressive 30s setInterval polling that
    // was refetching the entire feed (posts + lives + stories + suggestions)
    // even while the user was actively scrolling — the single biggest
    // culprit of mobile jank. Feed now refreshes only when the tab
    // becomes visible again OR on explicit data-changed events.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadFeed();
    };
    const onChanged = () => loadFeed();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('nymia:data-changed', onChanged);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('nymia:data-changed', onChanged);
      setTelemetryPage(null);
    };
  }, [loadFeed, peopleMode]);

  // Phase 49 · Infinite scroll sentinel — grows `visibleCount` in
  // steps until we exhaust the fetched batch.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_STEP, items.length));
        }
      }
    }, { rootMargin: '600px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [items.length]);

  // Reset the window whenever a fresh batch arrives.
  useEffect(() => {
    setVisibleCount(INITIAL_PAGE);
  }, [items]);

  // Memoize the actually rendered slice so `items` mutations that
  // don't affect the current window don't re-render the whole list.
  const visibleItems = useMemo(() => items.slice(0, visibleCount), [items, visibleCount]);

  // Phase F1 — flag gating: unknown → tiny spinner, ON → People Feed,
  // OFF → classic feed below (untouched).
  if (peopleMode === null) {
    return (
      <div className="flex justify-center py-16" data-testid="home-feed-loading">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
      </div>
    );
  }
  if (peopleMode) return <PeopleFeed />;

  return (
    <div className="mx-auto flex max-w-[975px] gap-8 px-0 pt-4 md:px-4">
      <main className="mx-auto w-full max-w-[470px]">
        {/* Stories row: ONLY users the viewer follows that have recent activity.
            Live creators come first, then those with a recent post (24h). */}
        <div className="relative">
          <div
            className="mb-4 flex gap-3 overflow-x-auto border-b border-border bg-card px-3 py-3 md:rounded-lg md:border md:border-border"
            data-testid="stories-row"
            onClickCapture={showRingTip ? dismissRingTip : undefined}
          >
            {stories.length === 0 ? (
              <button
                onClick={() => navigate('/explore')}
                className="flex w-full items-center justify-center gap-2 py-2 text-xs text-muted-foreground hover:text-foreground"
                data-testid="stories-empty"
              >
                <UserPlus className="h-4 w-4" />
                {t('stories_row.empty')}
              </button>
            ) : (
              stories.map((s) => <StoryRing key={s.user_id} story={s} />)
            )}
          </div>
          {/* NeoUI — first-visit tap hint anchored to the first ring */}
          {showRingTip && stories.length > 0 && (
            <button
              type="button"
              onClick={dismissRingTip}
              className="absolute left-5 top-[86px] z-20 animate-bounce rounded-full bg-amber-500 px-3 py-1.5 text-[11px] font-bold text-black shadow-lg"
              data-testid="ring-tap-tooltip"
            >
              <span className="absolute -top-1.5 left-6 h-3 w-3 rotate-45 bg-amber-500" />
              {t('stories_row.tap_tip')}
            </button>
          )}
        </div>

        {/* Upcoming lives banner */}
        <UpcomingLivesBanner upcoming={liveData.upcoming} />

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-amber-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-border bg-card px-6 py-14 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-amber-400">
              <Camera className="h-8 w-8 gold-text" strokeWidth={1.4} />
            </div>
            <h3 className="text-xl font-semibold">{t('empty_feed.title')}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('empty_feed.description')}
            </p>
            <button
              onClick={() => navigate('/create')}
              className="mt-4 text-sm font-semibold gold-text hover:underline"
            >
              {t('empty_feed.cta')}
            </button>
          </div>
        ) : (
          <>
            {visibleItems.map((it, idx) =>
              it.type === 'live'
                ? <LiveFeedCard key={`live-${it.data.id}-${idx}`} live={it.data} />
                : it.type === 'system'
                  ? <SystemFeedCard key={`sys-${it.data.id}`} item={it.data} />
                  : <PostCard
                    key={`post-${it.data.id}`}
                    post={it.data}
                    onHidden={(pid) => setItems((prev) => prev.filter((x) => !(x.type === 'post' && x.data?.id === pid)))}
                    onUpdated={(p) => setItems((prev) => prev.map((x) => (x.type === 'post' && x.data?.id === p.id ? { ...x, data: p } : x)))}
                  />
            )}
            {/* Phase 49 · Infinite scroll sentinel */}
            {visibleCount < items.length && (
              <div
                ref={sentinelRef}
                className="flex justify-center py-6"
                data-testid="feed-load-more-sentinel"
              >
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-amber-400" />
              </div>
            )}
          </>
        )}
      </main>

      <aside className="hidden w-[320px] shrink-0 pt-4 lg:block">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar src={user?.avatar_url} alt={user?.username} size={56} />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-semibold">{user?.username}</span>
              <span className="text-sm text-muted-foreground">{user?.full_name}</span>
            </div>
          </div>
          <button className="text-xs font-semibold gold-text">{t('sidebar.switch')}</button>
        </div>

        {/* Live now mini panel in sidebar */}
        {liveData.live_now.length > 0 && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-gradient-to-br from-red-900/20 via-neutral-900 to-rose-900/20 p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-400">
                <Radio className="h-3.5 w-3.5" /> {t('live_now.title')}
              </p>
              <button
                onClick={() => navigate('/lives')}
                className="text-[10px] font-semibold text-red-400 hover:text-red-300"
              >
                {t('live_now.see_all')}
              </button>
            </div>
            <div className="flex flex-col gap-1.5">
              {liveData.live_now.slice(0, 3).map((live) => (
                <LiveCard key={live.id} live={live} compact />
              ))}
            </div>
          </div>
        )}

        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-muted-foreground">{t('sidebar.suggested')}</span>
        </div>
        {suggestions.length === 0 ? (
          <p className="py-2 text-xs text-muted-foreground" data-testid="suggestions-empty">
            {t('sidebar.empty_suggestions')}
          </p>
        ) : (
          suggestions.map((s) => (
            <Suggestion key={s.user_id} s={s} onFollow={onFollowSuggestion} />
          ))
        )}

        <p className="mt-6 text-xs text-muted-foreground/70">
          {t('sidebar.meta_links')}
        </p>
        <p className="mt-3 text-xs uppercase tracking-wider text-muted-foreground/70">
          {t('sidebar.copyright', { year: new Date().getFullYear() })}
        </p>
      </aside>
    </div>
  );
};

export default HomePage;
